import React from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { GREENING_AGENT_LABEL } from '../constants';
import { GREENING_BRAND } from '../greeningFabActions';
import { GreeningBrandLabel } from '../GreeningBrandLabel';

interface Props {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Cápsula no formato do botão do chat. */
  framed?: boolean;
}

const PILL_WASH = `linear-gradient(135deg, ${alpha('#fff', 0.98)} 0%, ${alpha(GREENING_BRAND.green, 0.12)} 100%)`;
const TEXT_PLATE = `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`;

const textPlateSx = {
  width: 'fit-content',
  maxWidth: '100%',
  px: 1.25,
  py: 0.75,
  borderRadius: '16px',
  backgroundImage: TEXT_PLATE,
  textAlign: 'center',
};

function VoiceTitle({ title, framed }: { title: string; framed: boolean }) {
  if (!framed) {
    return (
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: GREENING_BRAND.green }}>
        {title}
      </Typography>
    );
  }

  const parts = title.split(GREENING_AGENT_LABEL);
  if (parts.length === 1) {
    return (
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, textAlign: 'center' }}>
        {title}
      </Typography>
    );
  }

  return (
    <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, textAlign: 'center' }}>
      {parts[0]}
      <GreeningBrandLabel fontWeight={800} onDark />
      {parts.slice(1).join(GREENING_AGENT_LABEL)}
    </Typography>
  );
}

/** Painel flutuante. `framed` usa a cápsula do botão do chat; o restante permanece uma caixa clara. */
export function GreeningVoiceOverlay({ open, title, subtitle, onClose, children, framed = false }: Props) {
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      role="region"
      aria-label={title}
      sx={{
        position: 'fixed',
        right: { xs: 16, sm: 24 },
        bottom: { xs: 16, sm: 24 },
        zIndex: 1600,
        width: { xs: 'calc(100% - 32px)', sm: 400 },
        // Nunca ultrapassar a altura da tela: cobre zoom/escala do SO no desktop
        // (Windows 125%, Ctrl++) e a barra de endereço no mobile (dvh). Era isso
        // que fazia a cápsula vazar/cortar e invadir a modal em alguns aparelhos.
        maxHeight: 'calc(100dvh - 48px)',
        color: 'text.primary',
        // Externo com overflow visível só para o X flutuante da cápsula aparecer
        // fora da caixa; o scroll e o recorte ficam no card interno.
        overflow: 'visible',
      }}
    >
      {framed ? (
        <IconButton
          aria-label="Fechar voz"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: -14,
            right: -14,
            zIndex: 1,
            color: 'text.secondary',
            bgcolor: '#fff',
            border: `1px solid ${alpha(GREENING_BRAND.green, 0.2)}`,
            boxShadow: `0 2px 8px ${alpha(GREENING_BRAND.green, 0.15)}`,
          }}
        >
          <Close />
        </IconButton>
      ) : null}
      <Box
        sx={{
          // Card: herda o teto de altura e rola por dentro quando não cabe,
          // recortando o conteúdo (ex.: a onda) nos cantos arredondados.
          maxHeight: 'inherit',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(framed
            ? {
                borderRadius: '40px',
                border: 'none',
                backgroundImage: PILL_WASH,
                outline: `2px solid ${alpha(GREENING_BRAND.green, 0.55)}`,
                boxShadow: `0 6px 20px ${alpha(GREENING_BRAND.green, 0.28)}`,
              }
            : {
                borderRadius: 2.5,
                bgcolor: '#fff',
                border: `1px solid ${alpha(GREENING_BRAND.teal, 0.28)}`,
                boxShadow: `0 16px 40px ${alpha(GREENING_BRAND.green, 0.18)}`,
              }),
        }}
      >
      <Box
        sx={
          framed
            ? {
                display: 'flex',
                justifyContent: 'center',
                px: 2,
                pt: 1.5,
                pb: 0,
              }
            : {
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                px: 2,
                pt: 1.5,
                pb: 1,
                borderBottom: `1px solid ${alpha(GREENING_BRAND.teal, 0.14)}`,
                background: `linear-gradient(90deg, ${alpha(GREENING_BRAND.green, 0.06)} 0%, ${alpha(GREENING_BRAND.teal, 0.06)} 100%)`,
              }
        }
      >
        <Box sx={{ minWidth: 0, ...(framed ? textPlateSx : {}) }}>
          <VoiceTitle title={title} framed={framed} />
          {subtitle ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: framed ? 0.25 : 0,
                lineHeight: 1.35,
                textAlign: framed ? 'center' : 'left',
                color: framed ? '#fff' : 'text.secondary',
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {framed ? null : (
          <IconButton aria-label="Fechar voz" onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          px: framed ? 1.25 : 1.75,
          pt: framed ? 0.75 : 1.75,
          pb: framed ? 3 : 1.75,
        }}
      >
        {children}
      </Box>
      </Box>
    </Box>,
    document.body,
  );
}
